import { useEffect, useState } from "react";
import type { AppSummary } from "../types/apps";
import { fetchApps } from "../services/api";

// Module-level cache — persists across remounts, cleared on page reload
let _cache: AppSummary[] | null = null;

interface UseAppsResult {
  apps: AppSummary[];
  loading: boolean;
  error: string | null;
}

export function useApps(): UseAppsResult {
  const [apps, setApps] = useState<AppSummary[]>(_cache ?? []);
  const [loading, setLoading] = useState(_cache === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (_cache !== null) {
      setApps(_cache);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchApps()
      .then((data) => {
        if (!cancelled) {
          _cache = data;
          setApps(data);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load apps");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { apps, loading, error };
}
